import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  FormControl,
  InputLabel,
  MenuItem,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

import { TimeEnum } from "../../enums/TimeEnum";

//Components
import TransactionButton from "../Common/TransactionButton";
import VisitTrackerList from "./VisitTrackerList";

//Common Styles
import {
  FilterContainer,
  FIlterHeader,
  FilterSection,
  FilterSelectEl,
  FilterTextField,
  FilterWrapper,
  SectionHeader,
  SectionTitle,
  StyledTable,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  Title,
  WorkspaceContainer,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";

//Svg
import { FilterIcon } from "../Svg";
import { CookService } from "../../services/CookServices";
import ConstructSequelizeFilters from "../../utils/ConstructSequelizeFilters";

//Legend
const legends = [
  { color: "#FFD700", label: "Important Visit" },
  { color: "#FCF55F", label: "Currently Tracked Visit" },
  { color: "skyblue", label: "First Visit" },
  { color: "#E97451", label: "Tracked But Check in not done" },
];

const VISIT_TYPE_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "One Meal Service", value: "ONE_MEAL_SERVICE" },
  { label: "Chef For Party", value: "CHEF_FOR_PARTY" },
];

export const ALLOWED_START_TIME = "06:00 AM";
export const ALLOWED_END_TIME = "09:00 PM";

/**
 * Returns TimeEnum values between 6:00 AM and 9:00 PM (inclusive)
 */
export const getAllowedTimeEnums = (): TimeEnum[] => {
  const start = moment(ALLOWED_START_TIME, "hh:mm A");
  const end = moment(ALLOWED_END_TIME, "hh:mm A");

  return Object.values(TimeEnum).filter((t) => {
    const time = moment(t, "hh:mm A");
    return time.isSameOrAfter(start) && time.isSameOrBefore(end);
  });
};

let cookService = new CookService();
const VisitTrackerTab = (props: any) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [date, setDate] = useState<any>(new Date());
  const [data, setData] = useState<any>([]);
  const [time, setTime] = useState<TimeEnum[]>([]);
  const [visitType, setVisitType] = useState<string>("ALL");
  const [cookIdInput, setCookIdInput] = useState<string>("");
  const [debouncedCookId, setDebouncedCookId] = useState<string>("");
  const [referenceIdInput, setReferenceIdInput] = useState<string>("");
  const [debouncedReferenceId, setDebouncedReferenceId] = useState<string>("");
  const shouldSkipDefaultTimeUpdate = useRef(false);


  useEffect(() => {
    if (!date) return;
    if (shouldSkipDefaultTimeUpdate.current) {
      shouldSkipDefaultTimeUpdate.current = false;
      return;
    }

    const selectedDate = moment(date).format("YYYY-MM-DD");
    const today = moment().format("YYYY-MM-DD");

    let newTime: TimeEnum[] = [];

    if (selectedDate === today) {
      let nextThreeSlots = getNextThreeTimeSlots();
      let lastSlot = nextThreeSlots[nextThreeSlots.length - 1];
      if (lastSlot) {
        newTime = Object.values(getAllowedTimeEnums()).filter(
          (t) => moment(t, "hh:mm A").isSameOrBefore(moment(lastSlot, "hh:mm A"))
        );
      }
    } else {
      newTime = Object.values(getAllowedTimeEnums());
    }

    // ✅ IMPORTANT: avoid unnecessary state updates
    setTime((prev) =>
      JSON.stringify(prev) === JSON.stringify(newTime) ? prev : newTime
    );

    setData([]);
  }, [date]);

  const getNextThreeTimeSlots = (): TimeEnum[] => {
    const now = moment();

    return Object.values(TimeEnum)
      .filter((t) => {
        const slotTime = moment(t, "hh:mm A");
        return slotTime.isAfter(now);
      })
      .slice(0, 3);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCookId(cookIdInput);
    }, 1000);
    return () => clearTimeout(timer);
  }, [cookIdInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReferenceId(referenceIdInput);
    }, 1000);
    return () => clearTimeout(timer);
  }, [referenceIdInput]);


  const loadData = useCallback(async () => {
    if (!date || !time || time.length === 0) return;

    setLoading(true);

    const filters: any = {
      date: moment(date).format("YYYY-MM-DD"),
      ...(debouncedCookId !== "" && { cookId: Number(debouncedCookId) }),
      ...(visitType !== "ALL" && { visitType }),
      time,
    };

    let constructedFilters = ConstructSequelizeFilters(
      filters,
      [],
      ["date", "cookId", 'visitType'],
      [],
      [],
      ["time"]
    );
    constructedFilters = {
      ...constructedFilters,
      ...(visitType !== "ALL" && { visitType }),
    }

    try {
      const res = await cookService.getAllTrackerVisits(constructedFilters);
      const filtered = debouncedReferenceId !== ""
        ? res.filter((item: any) => String(item?.visitData?.id) === debouncedReferenceId)
        : res;
      setData(filtered);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [date, debouncedCookId, debouncedReferenceId, time, visitType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <WorkspaceContainer>
      <FilterWrapper>
        <FIlterHeader>
          <FilterSection style={{ marginBottom: "0" }}>
            <SectionHeader>
              <WorkSpaceTitleRow>
                <FilterIcon color="#1976d2" height={20} width={20} />
                <SectionTitle>Filters</SectionTitle>
              </WorkSpaceTitleRow>
            </SectionHeader>

            <TransactionButton
              name={"Clear Filters"}
              onClickHandler={() => {
                shouldSkipDefaultTimeUpdate.current = true;
                setDate(new Date(moment().format("YYYY-MM-DD")));
                setCookIdInput("");
                setReferenceIdInput("");
                setTime([]);
                setData([]);
                setVisitType("ALL");
              }}
            />
          </FilterSection>
        </FIlterHeader>
        <FilterContainer>
          {/* <FilterInput
            value={cookIdInput}
            type="number"
            size="small"
            disabled={loading}
            variant="outlined"
            onChange={(event) => {
              const val = event.target.value;
              if (val === "" || (/^\d+$/.test(val) && Number(val) >= 0)) {
                setCookIdInput(val);
              }
            }}
            label="Cook Id"
            InputProps={{ inputProps: { min: 0 } }}
          /> */}


          <FilterTextField
            label="Reference ID"
            value={referenceIdInput}
            type="text"
            size="small"
            variant="outlined"
            onChange={(event) => {
              const val = event.target.value;
              if (val === "" || /^\d+$/.test(val)) {
                setReferenceIdInput(val);
              }
            }}
          />

          <FormControl variant="outlined" sx={{ minWidth: "175px" }}>
            <DatePicker
              label="Date"
              inputFormat="dd MMM yyyy"
              disableMaskedInput
              value={date}
              disabled={loading}
              onChange={(value) => {
                setDate(value);
                setTime([]);
                setData([]);
              }}
              renderInput={(params) => (
                <FilterTextField {...params} variant="outlined" sx={{ minWidth: "175px" }} />
              )}
            />
          </FormControl>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 140, width: '20%' }}>
            <InputLabel id="visit-type-select-label">Visit Type</InputLabel>
            <FilterSelectEl
              labelId="visit-type-select-label"
              label="Visit Type"
              value={visitType}
              disabled={loading}
              onChange={(e) => {
                setVisitType(e.target.value as string);
                setData([]);
              }}
            >
              {VISIT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </FilterSelectEl>
          </FormControl>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 140 , width: '100%'}}>
            <InputLabel id="time-select-label">Time</InputLabel>
            <FilterSelectEl
              labelId="time-select-label"
              label="Time"
              multiple
              value={time}
              disabled={loading}
              onChange={(e) => {
                const value = e.target.value as string | TimeEnum[];
                const nextTime = Array.isArray(value) ? value : value.split(",");
                setTime(nextTime.includes("") ? [] : (nextTime as TimeEnum[]));
                setData([]);
              }}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    width: "100%",
                  }}
                >
                  {(selected as TimeEnum[]).map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      size="small"
                      color="primary"
                    />
                  ))}
                </Box>
              )}
            >
              <MenuItem value="">CLEAR</MenuItem>
              {Object.values(getAllowedTimeEnums()).map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </FilterSelectEl>
          </FormControl>
        </FilterContainer>
      </FilterWrapper>
            <Box display="flex" flexDirection="row" alignItems="center" gap={2} m={2}>
        {legends.map((eachLegend) => (
          <Box
            key={eachLegend.label}
            sx={{ display: "flex", alignItems: "center" }}
            id={eachLegend.label}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: eachLegend.color,
                borderRadius: 1,
              }}
              mr={1}
            />
            <Typography fontSize={12}>{eachLegend.label}</Typography>
          </Box>
        ))}
      </Box>

      <TableWrapper>
        <TableTopHeader>
          <Title>Visit Details</Title>
        </TableTopHeader>

        <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
          <StyledTable>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "rgba(232, 235, 237, 0.3) !important",
                }}
              >
                <TableHeader>Late Checkin</TableHeader>
                <TableHeader>Allocator comments</TableHeader>
                <TableHeader>Update Tracker note</TableHeader>
                <TableHeader>Visit Id - Type</TableHeader>
                <TableHeader>Payment</TableHeader>
                <TableHeader>Address</TableHeader>
                <TableHeader>Tracking Status</TableHeader>
                <TableHeader>Cook</TableHeader>
                <TableHeader>Contact</TableHeader>
                <TableHeader>Visit Time</TableHeader>
                <TableHeader>PreCheckin</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody sx={{ backgroundColor: "red" }}>
              {data && data.length > 0 ? (
                data.map((trial: any) => (
                  <VisitTrackerList
                    key={`${trial?.referenceModelName}-${trial?.visitData?.id ?? trial?.id}`}
                    eachData={trial}
                    onDataChange={loadData}
                  />
                ))
              ) : (
                <TableRow>
                  <TableHeader
                    colSpan={8}
                    align="center"
                    sx={{ fontWeight: "bold", padding: "20px" }}
                  >
                    {loading ? "Loading..." : "No Visits Found"}
                  </TableHeader>
                </TableRow>
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>

      {/* <Box sx={{ width: "100%", mt: 2 }}>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"flex-end"}
          my={2}
          alignItems={"center"}
        >
          <TextField
            value={cookId}
            type={"number"}
            size={"small"}
            disabled={loading}
            onChange={(event) => {
              let val = event.target.value;
              if (val === "") {
                setCookId("");
              } else {
                if (/^-?\d*\.?\d*$/.test(val) && parseFloat(val) >= 0) {
                  setCookId(Number(event.target.value));
                }
              }
            }}
            sx={{ mr: 2 }}
            variant={"filled"}
            label={"Cook Id"}
            InputProps={{ inputProps: { min: 0 } }}
          />
          <FormControl sx={{ mr: 2, mt: "4px" }}>
            <DatePicker
              label="Date"
              inputFormat="dd MMM yyyy"
              disableMaskedInput
              value={date}
              disabled={loading}
              onChange={(value) => {
                setDate(value);
                setActiveIndex(0);
                setData([]);
                updateRouter("date", moment(value).format("YYYY-MM-DD"));
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </FormControl>
          <FormControl sx={{ marginRight: 2 }}>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              name="Service"
              label="Service"
              size={"small"}
              multiple={true}
              value={status}
              disabled={loading}
              open={menuOpen}
              onClose={handleClose}
              onOpen={handleOpen}
              onChange={(e) => {
                handleClose();
                setStatus(e.target.value);
                setActiveIndex(0);
                setData([]);
                updateRouter("status", JSON.stringify(e.target.value));
              }}
              sx={{ width: 150 }}
            >
              {Object.keys(TrialsV3Status).map((key, index) => (
                <MenuItem key={key} value={key}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ marginRight: 2 }}>
            <InputLabel id="visit-type-select-label">Visit Type</InputLabel>
            <Select
              name="Visit Type"
              label="Visit Type"
              size={"small"}
              value={visitType}
              disabled={loading}
              onChange={(e) => {
                setVisitType(e.target.value);
                setActiveIndex(0);
                setData([]);
                updateRouter("visitType", e.target.value);
              }}
              sx={{ width: 150 }}
            >
              <MenuItem key={"NULL"} value={""}>
                {"CLEAR_FILTER"}
              </MenuItem>
              <MenuItem key={"NEW_Trial"} value={"NEW_TRIAL"}>
                {"NEW_TRIAL"}
              </MenuItem>
              <MenuItem key={"OMS"} value={"OMS"}>
                {"One Meal Service"}
              </MenuItem>
              <MenuItem key={"Free_TR"} value={"FREE_TR"}>
                {"FREE TR"}
              </MenuItem>
              <MenuItem key={"PR_TRIAL"} value={"PR_TRIAL"}>
                {"PR TRIAL"}
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            value={visitId}
            type={"number"}
            size={"small"}
            disabled={loading}
            onChange={(event) => {
              let val = event.target.value;
              if (val === "") {
                setVisitId("");
                // updateRouter('visitId', event.target.value);
              } else {
                if (/^-?\d*\.?\d*$/.test(val) && parseFloat(val) >= 0) {
                  setVisitId(Number(event.target.value));
                  // updateRouter('visitId', event.target.value);
                }
              }
              // setActiveIndex(0);
              // setData([]);
            }}
            sx={{ mr: 2 }}
            variant={"filled"}
            label={"Visit Id"}
            InputProps={{ inputProps: { min: 0 } }}
          />
          <TransactionButton
            name={"Clear Filter"}
            onClickHandler={() => {
              setDate([]);
              setActiveIndex(0);
              const emptyParams = new URLSearchParams();
              navigate({ search: `?${emptyParams.toString()}` });
              setDate(new Date(moment().format("YYYY-MM-DD")));
              setStatus([
                TrialsV3Status.COOK_ALLOCATED,
                TrialsV3Status.COOK_CONFIRMED,
              ]);
              setVisitType(null);
              setVisitId("");
              setCookId("");
            }}
          />
        </Box>
        <Grid container spacing={1}>
          <Grid sm={3} md={3} xs={12} item>
            {!loading && !trialLoading && data?.length ? (
              <Box
                sx={{
                  border: "1px solid #d4d4d4",
                  height: "75vh",
                  overflowY: "scroll",
                  borderRadius: "6px",
                }}
              >
                {sortedData?.map((eachData, index: number) => {
                  return (
                    <Box
                      key={`${eachData?.id} - ${eachData?.entityType}`}
                      sx={{
                        p: 1,
                        border: "1px solid #e8e8e8",
                        cursor: "pointer",
                        position: "relative",
                        paddingTop: "35px",
                        margin: "6px",
                        borderRadius: "6px",
                        ...(activeIndex === index
                          ? { backgroundColor: "#cacaca" }
                          : {}),
                      }}
                      onClick={() => {
                        setActiveIndex(index);

                        navigate(
                          `/visit-details/${eachData.entityType}/${eachData.id}`,
                          {
                            state: {
                              visitData: eachData,
                            },
                          }
                        );
                      }}

                      //    onClick={() => { setActiveIndex(index) }}
                    >
                      <VisitTrackerCard eachData={eachData} />
                    </Box>
                  );
                })}
              </Box>
            ) : loading || trialLoading ? (
              <Box>
                {[1, 2, 3, 4, 5].map((d: number, i: number) => (
                  <Skeleton
                    key={d}
                    variant="rectangular"
                    width={"100%"}
                    height={120}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ p: 2, justifyContent: "center" }}>
                <TextRegular title={"NO SERVICES FOUND"} />
              </Box>
            )}
          </Grid>

          <Grid sm={9} md={9} xs={12} item>
            <Box
              sx={{
                border: "1px solid #d4d4d4",
                height: "75vh",
                overflowY: "scroll",
                p: 1,
                borderRadius: "6px",
              }}
            >
              {!loading && !trialLoading && data?.length ? (
                <CookLocationTracker
                  cookId={data?.[activeIndex]?.cookId}
                  visitData={data?.[activeIndex]}
                />
              ) : loading || trialLoading ? (
                <Box>
                  <Grid container spacing={2}>
                    <Grid md={4} sm={4} xs={12} item>
                      <Skeleton
                        variant="text"
                        width={"50%"}
                        sx={{ fontSize: "1.5rem", mt: 1 }}
                      />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton
                        variant="text"
                        width={"50%"}
                        sx={{ fontSize: "1.5rem", mt: 1 }}
                      />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    </Grid>
                    <Grid md={4} sm={4} xs={12} item>
                      <Skeleton
                        variant="text"
                        width={"50%"}
                        sx={{ fontSize: "1.5rem", mt: 1 }}
                      />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton
                        variant="text"
                        width={"50%"}
                        sx={{ fontSize: "1.5rem", mt: 1 }}
                      />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    </Grid>
                    <Grid md={4} sm={4} xs={12} item>
                      <Skeleton
                        variant="text"
                        width={"50%"}
                        sx={{ fontSize: "1.5rem" }}
                      />
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                      <Skeleton
                        variant="text"
                        sx={{ fontSize: "2rem", mt: 3 }}
                      />
                      <Skeleton variant="text" sx={{ fontSize: "2rem" }} />
                      <Skeleton variant="text" sx={{ fontSize: "2rem" }} />
                    </Grid>
                    <Grid md={12} sm={12} xs={12} item>
                      <Skeleton
                        variant="rectangular"
                        height={"300px"}
                        sx={{ mt: 3 }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                ""
              )}
            </Box>
          </Grid>
        </Grid>
      </Box> */}
    </WorkspaceContainer>
  );
};
export default VisitTrackerTab;
